<template>
  <AppLayout>
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Support Center
      </div>
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            How Can We Help?
          </h1>
          <p class="text-xl text-slate-300 mb-8 max-w-2xl">
            Get help with OilBuddy platform, find answers to common questions, and connect with our support team.
          </p>
        </div>
        <div class="flex items-center space-x-4">
          <button @click="startLiveChat" class="btn-primary-modern">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            Live Chat
          </button>
        </div>
      </div>
    </div>

    <!-- Quick Help Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="support-card group" @click="scrollToSection('faq')">
        <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
          <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">FAQ</h3>
        <p class="text-slate-400 text-sm">Find answers to common questions</p>
      </div>

      <div class="support-card group" @click="scrollToSection('contact')">
        <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition-colors">
          <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">Contact Us</h3>
        <p class="text-slate-400 text-sm">Send us a message</p>
      </div>

      <div class="support-card group" @click="openDocumentation">
        <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
          <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">Documentation</h3>
        <p class="text-slate-400 text-sm">User guides and tutorials</p>
      </div>

      <div class="support-card group" @click="openVideoTutorials">
        <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
          <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">Video Tutorials</h3>
        <p class="text-slate-400 text-sm">Step-by-step guides</p>
      </div>
    </div>

    <!-- FAQ Section -->
    <div id="faq" class="mb-12">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Frequently Asked Questions</h2>
        <div class="flex items-center space-x-4">
          <input 
            v-model="faqSearch" 
            type="text" 
            placeholder="Search FAQ..." 
            class="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
        </div>
      </div>

      <div class="space-y-4">
        <div 
          v-for="(category, categoryKey) in filteredFAQ" 
          :key="categoryKey" 
          class="support-card"
        >
          <h3 class="text-xl font-semibold text-white mb-4">{{ category.title }}</h3>
          <div class="space-y-3">
            <div 
              v-for="(item, index) in category.items" 
              :key="index"
              class="border border-slate-700/50 rounded-lg overflow-hidden"
            >
              <button 
                @click="toggleFAQ(categoryKey, index)"
                class="w-full px-4 py-3 text-left bg-slate-800/30 hover:bg-slate-700/30 transition-colors flex items-center justify-between"
              >
                <span class="font-medium text-white">{{ item.question }}</span>
                <svg 
                  class="w-5 h-5 text-slate-400 transition-transform" 
                  :class="{ 'rotate-180': expandedFAQ[`${categoryKey}-${index}`] }"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div 
                v-show="expandedFAQ[`${categoryKey}-${index}`]"
                class="px-4 py-3 bg-slate-800/20 border-t border-slate-700/50"
              >
                <p class="text-slate-300">{{ item.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Form Section -->
    <div id="contact" class="mb-12">
      <h2 class="text-2xl font-bold text-white mb-6">Contact Support</h2>
      
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Contact Form -->
        <div class="support-card">
          <h3 class="text-xl font-semibold text-white mb-4">Send us a Message</h3>
          <form @submit.prevent="submitContactForm" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Subject</label>
              <select 
                v-model="contactForm.subject" 
                class="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              >
                <option value="">Select a subject</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Question</option>
                <option value="feature">Feature Request</option>
                <option value="general">General Inquiry</option>
                <option value="bug">Bug Report</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Priority</label>
              <select 
                v-model="contactForm.priority" 
                class="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Message</label>
              <textarea 
                v-model="contactForm.message" 
                rows="6"
                placeholder="Describe your issue or question..."
                class="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
              ></textarea>
            </div>

            <div class="flex items-center space-x-4">
              <label class="flex items-center">
                <input 
                  v-model="contactForm.includeScreenshot" 
                  type="checkbox" 
                  class="mr-2 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500/50"
                >
                <span class="text-sm text-slate-300">Include screenshot</span>
              </label>
            </div>

            <button 
              type="submit" 
              :disabled="isSubmitting"
              class="btn-primary-modern w-full"
            >
              <svg v-if="isSubmitting" class="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              {{ isSubmitting ? 'Sending...' : 'Send Message' }}
            </button>
          </form>
        </div>

        <!-- Contact Information -->
        <div class="space-y-6">
          <div class="support-card">
            <h3 class="text-xl font-semibold text-white mb-4">Contact Information</h3>
            <div class="space-y-4">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">Email Support</p>
                  <p class="text-slate-400 text-sm">support@oilbuddy.com</p>
                </div>
              </div>

              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">Phone Support</p>
                  <p class="text-slate-400 text-sm">1-800-OIL-BUDDY</p>
                </div>
              </div>

              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">Response Time</p>
                  <p class="text-slate-400 text-sm">Within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          <div class="support-card">
            <h3 class="text-xl font-semibold text-white mb-4">Support Hours</h3>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-slate-300">Monday - Friday</span>
                <span class="text-white">8:00 AM - 6:00 PM CST</span>
              </div>
              <div class="flex justify-between">
                <span class="text-slate-300">Saturday</span>
                <span class="text-white">9:00 AM - 3:00 PM CST</span>
              </div>
              <div class="flex justify-between">
                <span class="text-slate-300">Sunday</span>
                <span class="text-white">Closed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Live Chat Widget -->
    <div v-if="showLiveChat" class="fixed bottom-4 right-4 z-50">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl w-80">
        <div class="p-4 border-b border-slate-700/50">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Live Chat</p>
                <p class="text-emerald-400 text-sm">Online</p>
              </div>
            </div>
            <button @click="closeLiveChat" class="text-slate-400 hover:text-white">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="h-64 overflow-y-auto p-4 space-y-3">
          <div v-for="(message, index) in chatMessages" :key="index" class="flex" :class="message.sender === 'user' ? 'justify-end' : 'justify-start'">
            <div 
              class="max-w-xs px-3 py-2 rounded-lg"
              :class="message.sender === 'user' ? 'bg-emerald-500 text-white' : 'bg-slate-700/50 text-white'"
            >
              <p class="text-sm">{{ message.text }}</p>
            </div>
          </div>
        </div>
        
        <div class="p-4 border-t border-slate-700/50">
          <div class="flex space-x-2">
            <input 
              v-model="chatInput" 
              @keyup.enter="sendChatMessage"
              type="text" 
              placeholder="Type your message..."
              class="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
            <button @click="sendChatMessage" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const router = useRouter()
const userStore = useUserStore()

// Reactive data
const faqSearch = ref('')
const expandedFAQ = ref<Record<string, boolean>>({})
const isSubmitting = ref(false)
const showLiveChat = ref(false)
const chatInput = ref('')
const chatMessages = ref([
  { sender: 'support', text: 'Hello! How can I help you today?' }
])

const contactForm = ref({
  subject: '',
  priority: 'medium',
  message: '',
  includeScreenshot: false
})

// FAQ Data
const faqData = {
  general: {
    title: 'General Questions',
    items: [
      {
        question: 'What is OilBuddy?',
        answer: 'OilBuddy is a comprehensive AI-powered platform designed specifically for oilfield contractors. It helps manage jobs, timesheets, payments, safety compliance, and provides AI assistance for various tasks.'
      },
      {
        question: 'How do I get started?',
        answer: 'Simply create an account, complete your profile with certifications, and start exploring the platform. You can begin by browsing available jobs, setting up your timesheets, or using the AI assistant.'
      },
      {
        question: 'Is OilBuddy free to use?',
        answer: 'OilBuddy offers both free and premium plans. The free plan includes basic features, while premium plans offer advanced AI capabilities, priority support, and additional tools.'
      }
    ]
  },
  technical: {
    title: 'Technical Support',
    items: [
      {
        question: 'How do I reset my password?',
        answer: 'Go to the login page and click "Forgot Password". Enter your email address and follow the instructions sent to your email to reset your password.'
      },
      {
        question: 'Why can\'t I access certain features?',
        answer: 'Some features may require specific permissions or account verification. Check your account status and contact support if you believe you should have access to certain features.'
      },
      {
        question: 'How do I update my profile information?',
        answer: 'Navigate to Profile & Certifications in the sidebar, then click "Edit Profile" to update your personal information, certifications, and preferences.'
      }
    ]
  },
  billing: {
    title: 'Billing & Payments',
    items: [
      {
        question: 'How do I view my payment history?',
        answer: 'Go to the Payments section in the sidebar to view your complete payment history, including pending payments and transaction details.'
      },
      {
        question: 'What payment methods are accepted?',
        answer: 'We accept major credit cards, bank transfers, and digital payment methods. Payment options may vary based on your location and account type.'
      },
      {
        question: 'How do I update my billing information?',
        answer: 'Navigate to Account Settings in the sidebar, then go to the Billing section to update your payment methods and billing address.'
      }
    ]
  },
  ai: {
    title: 'AI Assistant',
    items: [
      {
        question: 'How does the AI Assistant work?',
        answer: 'The AI Assistant uses advanced language models to help with document creation, email writing, compliance checking, and other tasks. Simply describe what you need, and the AI will generate appropriate content.'
      },
      {
        question: 'What types of documents can the AI create?',
        answer: 'The AI can help create safety reports, job proposals, email templates, compliance documents, and various other oilfield-related documents.'
      },
      {
        question: 'Is my data secure with the AI?',
        answer: 'Yes, all data processed by the AI is encrypted and secure. We follow strict privacy protocols and never share your information with third parties.'
      }
    ]
  }
}

// Computed properties
const filteredFAQ = computed(() => {
  if (!faqSearch.value) return faqData
  
  const search = faqSearch.value.toLowerCase()
  const filtered: any = {}
  
  Object.entries(faqData).forEach(([key, category]) => {
    const filteredItems = category.items.filter(item => 
      item.question.toLowerCase().includes(search) || 
      item.answer.toLowerCase().includes(search)
    )
    
    if (filteredItems.length > 0) {
      filtered[key] = {
        title: category.title,
        items: filteredItems
      }
    }
  })
  
  return filtered
})

// Methods
const toggleFAQ = (categoryKey: string, index: number) => {
  const key = `${categoryKey}-${index}`
  expandedFAQ.value[key] = !expandedFAQ.value[key]
}

const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

const submitContactForm = async () => {
  if (!contactForm.value.subject || !contactForm.value.message) {
    alert('Please fill in all required fields')
    return
  }
  
  isSubmitting.value = true
  
  try {
    const response = await fetch('/api/support/ticket', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject: contactForm.value.subject,
        priority: contactForm.value.priority,
        message: contactForm.value.message,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.success === false) {
      throw new Error(data.error || 'Failed to send message')
    }
    
    contactForm.value = {
      subject: '',
      priority: 'medium',
      message: '',
      includeScreenshot: false
    }
    
    alert('Your message has been sent. An admin will reply from the support inbox.')
  } catch (error) {
    alert('There was an error sending your message. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const startLiveChat = () => {
  showLiveChat.value = true
}

const closeLiveChat = () => {
  showLiveChat.value = false
}

const sendChatMessage = () => {
  if (!chatInput.value.trim()) return
  
  // Add user message
  chatMessages.value.push({
    sender: 'user',
    text: chatInput.value
  })
  
  const userMessage = chatInput.value
  chatInput.value = ''
  
  // Simulate support response
  setTimeout(() => {
    const responses = [
      'Thank you for your message. A support representative will be with you shortly.',
      'I understand your concern. Let me help you with that.',
      'That\'s a great question! Here\'s what you need to know...',
      'I\'ll look into this for you right away.'
    ]
    
    chatMessages.value.push({
      sender: 'support',
      text: responses[Math.floor(Math.random() * responses.length)]
    })
  }, 1000)
}

const openDocumentation = () => {
  window.open('/documentation', '_blank')
}

const openVideoTutorials = () => {
  window.open('/tutorials', '_blank')
}

onMounted(() => {
  // Initialize any necessary data
})
</script>

<style scoped>
.support-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:bg-slate-700/40 transition-all duration-200 cursor-pointer;
}

.btn-primary-modern {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40;
}

.btn-primary-modern:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>
